'use client';

import { Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useRef } from 'react';

import { TrackedButton, ValidatedPhoneInput } from '@/components';
import { useFooter } from '@/contexts';
import { AuthMachineEvent } from '@/machines';

interface PhoneNumberFormProps {
  send: (event: AuthMachineEvent) => void;
  isLoading?: boolean;
  phoneNumber?: string;
}

interface PhoneNumberFormValues {
  phoneNumber?: string;
}

const PhoneNumberForm = ({ send, phoneNumber }: PhoneNumberFormProps) => {
  const buttonText = 'CONTINUE';

  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<PhoneNumberFormValues>({
    initialValues: {
      phoneNumber,
    },
    validate: {
      phoneNumber: (value) => {
        if (!value) return 'Phone number is required';

        // E.164 format check for US numbers (+1XXXXXXXXXX)
        if (!/^\+1[0-9]{10}$/.test(value.replace(/\s+/g, ''))) {
          return 'Enter a valid phone number';
        }
        return null;
      },
    },
  });

  const handleSubmit = useCallback(
    async (values: PhoneNumberFormValues) => {
      notifications.clean();
      const phoneNumber = values.phoneNumber ?? '';

      // First send the event to update the phone number in the state machine
      send({
        type: 'SUBMIT_PHONE',
        phoneNumber,
      });
    },
    [send],
  );

  const { buttonPrimaryProps, setButtonPrimaryProps, clearButtonPrimaryProps } =
    useFooter();

  useEffect(() => {
    setButtonPrimaryProps({
      children: buttonText,
      type: 'submit',
      variant: 'primary',
      trackingName: 'PhoneNumberForm_continue_button',
      onClick: () => formRef?.current?.requestSubmit(),
    });

    return () => clearButtonPrimaryProps();
  }, [buttonText, setButtonPrimaryProps, clearButtonPrimaryProps]);

  return (
    <form
      ref={formRef}
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <Stack gap="xl">
        <ValidatedPhoneInput
          {...form.getInputProps('phoneNumber')}
          value={form.values.phoneNumber}
          onChange={(value: string) => form.setFieldValue('phoneNumber', value)}
          error={form.errors.phoneNumber}
          placeholder="Phone Number"
          required
          autoFocus
        />

        <TrackedButton
          fullWidth
          size="lg"
          visibleFrom="md"
          {...buttonPrimaryProps}
        />
      </Stack>
    </form>
  );
};

export { PhoneNumberForm };
